/**
 * Приёмка поставки с «Мой день» без связи (МП-02, ПР-08, ПМ-09): черновик на телефоне, уходит сам при появлении связи
 * (тело + фото накладной/паспорта). Каждая приёмка отправляется один раз: постоянный ключ повтора, каждое
 * загруженное фото запоминается, перед загрузкой ищется уже загруженный свой файл с тем же содержимым; отправка —
 * только от имени того, кто приёмку набрал.
 */
import {
  SiteDeliveryAcceptAck,
  type SiteDeliveryAcceptInput,
  type SitePhotoFileLookup,
} from '@epc/contracts';
import { ApiError, api } from '../../../lib/api';

export type PendingDeliveryPhoto = {
  id: string;
  name: string;
  type: string;
  blob: Blob;
};

/** Тело приёмки без уже загруженных fileId — фото лежат в IndexedDB до sync. */
export type PendingDeliveryBody = Omit<SiteDeliveryAcceptInput, 'photoFileIds'>;

export type PendingDelivery = {
  id: string;
  scope: string;
  projectId: string;
  scheduleRowId: string;
  body: PendingDeliveryBody;
  photos: PendingDeliveryPhoto[];
  createdAt: number;
  state: 'pending' | 'sending' | 'sent' | 'error';
  error: string | null;
  serverAcceptanceId: string | null;
  /** Ключ повтора команды; у записей старых версий появляется перед первой отправкой */
  clientOperationId?: string;
  /** Фото, уже загруженные на сервер: id фото на телефоне → fileId */
  uploaded?: Record<string, string>;
  /** Что принимается — для экрана: «Цемент М400 · договор МД-12» */
  label?: string;
};

export type DeliveryScope = { tenantId: string; userId: string };

const scopeKey = (s: DeliveryScope) => JSON.stringify([s.tenantId, s.userId]);

export class PendingDeliveriesStore {
  constructor(private readonly db: IDBDatabase) {}

  static open(
    factory: IDBFactory = indexedDB,
    name = 'epc-site-deliveries-offline',
  ): Promise<PendingDeliveriesStore> {
    return new Promise((resolve, reject) => {
      const request = factory.open(name, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('deliveries')) {
          const store = request.result.createObjectStore('deliveries', { keyPath: 'id' });
          store.createIndex('scope', 'scope');
        }
      };
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        request.result.onversionchange = () => request.result.close();
        resolve(new PendingDeliveriesStore(request.result));
      };
    });
  }

  close() {
    this.db.close();
  }

  list(scope: DeliveryScope): Promise<PendingDelivery[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('deliveries', 'readonly');
      const req = tx.objectStore('deliveries').index('scope').getAll(scopeKey(scope));
      tx.oncomplete = () => {
        const rows = (req.result as PendingDelivery[]).filter((r) => r.state !== 'sent');
        rows.sort((a, b) => a.createdAt - b.createdAt);
        resolve(rows);
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  put(row: PendingDelivery): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('deliveries', 'readwrite');
      tx.objectStore('deliveries').put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  remove(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction('deliveries', 'readwrite');
      tx.objectStore('deliveries').delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

export async function enqueuePendingDelivery(
  store: PendingDeliveriesStore,
  scope: DeliveryScope,
  projectId: string,
  scheduleRowId: string,
  body: PendingDeliveryBody,
  photos: PendingDeliveryPhoto[],
  now = Date.now(),
  label?: string,
): Promise<PendingDelivery> {
  const row: PendingDelivery = {
    id: crypto.randomUUID(),
    scope: scopeKey(scope),
    projectId,
    scheduleRowId,
    body,
    photos,
    createdAt: now,
    state: 'pending',
    error: null,
    serverAcceptanceId: null,
    clientOperationId: crypto.randomUUID(),
    uploaded: {},
    ...(label ? { label } : {}),
  };
  await store.put(row);
  return row;
}

export type DeliverySyncResult = {
  sent: number;
  failed: number;
  left: number;
  /** Принятые в этом проходе: id записи на телефоне → замечание поставщику, если создано */
  accepted?: Record<string, { remarkId: string | null }>;
};

const activeSyncs = new Map<string, Promise<DeliverySyncResult>>();

/**
 * Одна отправка на владельца очереди, в том числе из разных вкладок (navigator.locks; без них — одна на вкладку).
 * isCurrentIdentity — вошедший сейчас человек тот же, что набрал приёмку: иначе отправка останавливается.
 */
export function syncPendingDeliveries(
  store: PendingDeliveriesStore,
  scope: DeliveryScope,
  isCurrentIdentity: () => boolean = () => true,
): Promise<DeliverySyncResult> {
  const key = JSON.stringify(['epc-site-deliveries-sync', scopeKey(scope)]);
  const active = activeSyncs.get(key);
  if (active) return active;
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  const run = (async (): Promise<DeliverySyncResult> =>
    locks
      ? locks.request(key, () => sendPendingDeliveries(store, scope, isCurrentIdentity))
      : sendPendingDeliveries(store, scope, isCurrentIdentity))();
  const pending = run.finally(() => activeSyncs.delete(key));
  activeSyncs.set(key, pending);
  return pending;
}

async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Свой файл с тем же содержимым уже на сервере (ответ на прошлую загрузку потерялся) */
async function findUploaded(blob: Blob, scope: DeliveryScope): Promise<string | null> {
  try {
    const found = await api<SitePhotoFileLookup>('/site/photo-files/lookup', {
      query: { sha256: await sha256Hex(blob), size: blob.size },
      expectedIdentity: scope,
    });
    return found.fileId;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return null;
    throw e;
  }
}

async function sendPendingDeliveries(
  store: PendingDeliveriesStore,
  scope: DeliveryScope,
  isCurrentIdentity: () => boolean,
): Promise<DeliverySyncResult> {
  const assertIdentity = () => {
    if (!isCurrentIdentity())
      throw new ApiError(
        409,
        'session_context_changed',
        'Пользователь или организация изменились. Приёмка оставлена прежнему владельцу.',
      );
  };
  // Очередь читается внутри блокировки: другая вкладка могла её уже отправить
  const queue = await store.list(scope);
  let sent = 0;
  let failed = 0;
  const accepted: Record<string, { remarkId: string | null }> = {};
  for (const row of queue) {
    if (row.state === 'sent' || row.state === 'error') continue;
    // Ключ и прогресс сохраняются до первого запроса: повтор после обрыва идёт с тем же ключом
    let progress: PendingDelivery = {
      ...row,
      state: 'sending',
      error: null,
      clientOperationId: row.clientOperationId ?? crypto.randomUUID(),
      uploaded: { ...(row.uploaded ?? {}) },
    };
    await store.put(progress);
    try {
      for (const photo of row.photos) {
        if (progress.uploaded![photo.id]) continue;
        assertIdentity();
        let fileId = await findUploaded(photo.blob, scope);
        // Тот же снимок приложен дважды — второй раз не ссылаемся на тот же файл
        if (fileId && Object.values(progress.uploaded!).includes(fileId)) fileId = null;
        if (!fileId) {
          const form = new FormData();
          form.append('kind', 'site_delivery_photo');
          form.append('file', photo.blob, photo.name || 'photo.jpg');
          fileId = (await api<{ id: string }>('/files', { method: 'POST', form, expectedIdentity: scope }))
            .id;
        }
        progress = { ...progress, uploaded: { ...progress.uploaded, [photo.id]: fileId } };
        await store.put(progress);
      }
      assertIdentity();
      const reply = await api<unknown>(
        `/site/projects/${row.projectId}/deliveries/${row.scheduleRowId}/accept-commands`,
        {
          method: 'POST',
          body: {
            clientOperationId: progress.clientOperationId,
            expectedRevision: 0,
            payload: {
              receivedQuantity: row.body.receivedQuantity,
              waybillNumber: row.body.waybillNumber ?? null,
              passportPresent: row.body.passportPresent,
              note: row.body.note ?? null,
              photoFileIds: row.photos.map((p) => progress.uploaded![p.id]!),
            },
          },
          idempotencyKey: progress.clientOperationId,
          expectedIdentity: scope,
        },
      );
      const ack = SiteDeliveryAcceptAck.safeParse(reply);
      if (!ack.success) throw new Error('Портал не подтвердил приёмку');
      await store.put({ ...progress, state: 'sent', serverAcceptanceId: ack.data.result.id, error: null });
      await store.remove(row.id);
      accepted[row.id] = { remarkId: ack.data.result.remarkId };
      sent += 1;
    } catch (e) {
      // Нет связи, вход истёк, «глазами пользователя», сменился человек — запись ждёт, а не становится ошибкой
      const paused =
        e instanceof ApiError &&
        (e.status === 401 ||
          e.code === 'session_context_changed' ||
          e.code === 'command_in_progress' ||
          (e.status === 403 && e.code === 'view_as'));
      const offline = paused || isOfflineApiError(e);
      const message = e instanceof Error ? e.message : 'Не удалось отправить';
      await store.put({ ...progress, state: offline ? 'pending' : 'error', error: message });
      if (offline) break;
      failed += 1;
    }
  }
  const left = (await store.list(scope)).length;
  return { sent, failed, left, accepted };
}

/** Отклонённую приёмку отправить заново: тот же ключ — сервер не создаст вторую запись */
export async function retryPendingDelivery(
  store: PendingDeliveriesStore,
  row: PendingDelivery,
): Promise<void> {
  await store.put({ ...row, state: 'pending', error: null });
}

/** Ошибка сети / нет связи — можно сохранить на телефон. */
export function isOfflineApiError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 0 || error.status === 429 || error.status >= 500;
}
